// RichTextContent.js
import React from 'react';
import DOMPurify from 'dompurify';

const RichTextContent = ({ htmlContent, className, component = 'div' }) => {
    const Tag = component;
    
    // Function to fix Quill's mixed list structure
    const fixQuillLists = (html) => {
        // Create a temporary DOM element to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Find all ol elements that contain mixed data-list attributes
        const olElements = tempDiv.querySelectorAll('ol');
        
        olElements.forEach(ol => {
            const listItems = Array.from(ol.querySelectorAll('li'));
            
            // Group items by their data-list attribute
            const bulletItems = listItems.filter(li => li.getAttribute('data-list') === 'bullet');
            const orderedItems = listItems.filter(li => li.getAttribute('data-list') === 'ordered');
            
            // Check if we need to fix the structure
            const hasBulletItems = bulletItems.length > 0;
            const hasOrderedItems = orderedItems.length > 0;
            
            if (hasBulletItems && hasOrderedItems) {
                // Mixed types: split into separate lists
                const container = document.createElement('div');
                
                // Add bullet list
                const ul = document.createElement('ul');
                bulletItems.forEach(item => {
                    const cleanItem = item.cloneNode(true);
                    cleanItem.removeAttribute('data-list');
                    const qlUiSpans = cleanItem.querySelectorAll('span.ql-ui');
                    qlUiSpans.forEach(span => span.remove());
                    ul.appendChild(cleanItem);
                });
                container.appendChild(ul);
                
                // Add ordered list
                const newOl = document.createElement('ol');
                orderedItems.forEach(item => {
                    const cleanItem = item.cloneNode(true);
                    cleanItem.removeAttribute('data-list');
                    const qlUiSpans = cleanItem.querySelectorAll('span.ql-ui');
                    qlUiSpans.forEach(span => span.remove());
                    newOl.appendChild(cleanItem);
                });
                container.appendChild(newOl);
                
                // Replace the original ol with our new structure
                ol.parentNode.replaceChild(container, ol);
            } else if (hasBulletItems && !hasOrderedItems) {
                // All items are bullets: convert ol to ul
                const ul = document.createElement('ul');
                bulletItems.forEach(item => {
                    const cleanItem = item.cloneNode(true);
                    cleanItem.removeAttribute('data-list');
                    const qlUiSpans = cleanItem.querySelectorAll('span.ql-ui');
                    qlUiSpans.forEach(span => span.remove());
                    ul.appendChild(cleanItem);
                });
                ol.parentNode.replaceChild(ul, ol);
            } else if (!hasBulletItems && hasOrderedItems) {
                // All items are ordered: just clean up attributes
                orderedItems.forEach(item => {
                    item.removeAttribute('data-list');
                    const qlUiSpans = item.querySelectorAll('span.ql-ui');
                    qlUiSpans.forEach(span => span.remove());
                });
            } else {
                // No data-list attributes or other cases: just clean up
                listItems.forEach(item => {
                    item.removeAttribute('data-list');
                    const qlUiSpans = item.querySelectorAll('span.ql-ui');
                    qlUiSpans.forEach(span => span.remove());
                });
            }
        });
        
        return tempDiv.innerHTML;
    };
    
    // Configure DOMPurify to preserve list structure
    const config = {
        ALLOWED_TAGS: [
            'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', // Preserve list tags
            'a', 'img', 'blockquote', 'pre', 'code',
            'div', 'span', 'sup', 'sub'
        ],
        ALLOWED_ATTR: [
            'href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style',
            'type' // Allow type attribute for ordered lists
        ],
        KEEP_CONTENT: true,
        RETURN_DOM: false,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM_IMPORT: false
    };
    
    // Fix Quill's mixed list structure before sanitizing
    const fixedHtml = fixQuillLists(htmlContent);
    const sanitizedContent = DOMPurify.sanitize(fixedHtml, config);
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
};

export default RichTextContent;
